import type { ReactNode } from 'react';
import '@/components/ui/FicharioTabs/FicharioTabs.css';

export interface FicharioTab {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: number | string;
}

export interface FicharioTabsProps {
  tabs: FicharioTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  padding?: string;
  className?: string;
}

/**
 * FicharioTabs — Abas estilo Chrome/fichário com curvas invertidas
 *
 * @param {Object} props
 * @param {Array} props.tabs - Array de abas: { id, label, icon?, badge? }
 *   - id: string — identificador único da aba
 *   - label: string | ReactNode — texto ou conteúdo da aba
 *   - icon: ReactNode? — ícone Lucide (ex: <FileText size={13} />)
 *   - badge: number | string? — contador exibido como badge
 * @param {string} props.activeTab - ID da aba ativa
 * @param {Function} props.onTabChange - Callback (tabId) => void
 * @param {string} [props.padding] - Padding horizontal do container (default: '0 20px')
 * @param {string} [props.className] - Classes CSS adicionais no container
 *
 * @example
 * <FicharioTabs
 *   tabs={[
 *     { id: 'dados', label: 'Dados' },
 *     { id: 'orcamentos', label: 'Orçamentos', icon: <FileText size={13} />, badge: 3 },
 *     { id: 'docs', label: 'Documentos', icon: <Paperclip size={13} />, badge: 2 },
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 * />
 */
export default function FicharioTabs({ tabs, activeTab, onTabChange, padding, className }: FicharioTabsProps) {
  if (!tabs || tabs.length <= 1) return null;

  return (
    <div
      className={`fichario-tabs${className ? ` ${className}` : ''}`}
      style={padding ? { padding } : undefined}
    >
      {tabs.map((tab: FicharioTab) => {
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            className={`fichario-tab${isActive ? ' fichario-tab--active' : ''}`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.icon && (
              <span className="fichario-tab__icon">{tab.icon}</span>
            )}
            {tab.label}
            {tab.badge != null && tab.badge !== 0 && tab.badge !== '' && (
              <span className="fichario-tab__badge">{tab.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
